import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  useCashRegistersQuery,
  useLockCashRegisterMutation,
} from "@/query/cashRegisterQuery"
import { CashRegisterTable } from "@okeys/shared-utils"
import { CheckCircle, Lock, Monitor, Plus, Terminal, Wifi } from "lucide-react"
import React, { useState } from "react"

type CashRegisterDisconnectedProps = {
  onOpenAddModal: () => void
}

export const CashRegisterDisconnected: React.FC<
  CashRegisterDisconnectedProps
> = ({ onOpenAddModal }) => {
  const { cashRegisters, pendingCashRegisters } = useCashRegistersQuery()
  const lockCashRegisterMutation = useLockCashRegisterMutation()
  const [connectingCashRegisterId, setConnectingCashRegisterId] = useState<
    string | null
  >(null)

  const handleConnectToCashRegister = async (
    cashRegister: CashRegisterTable
  ) => {
    setConnectingCashRegisterId(cashRegister.caisseId)

    try {
      await lockCashRegisterMutation.mutateAsync({
        caisseId: cashRegister.caisseId,
      })
    } catch (error) {
      console.error("Erreur lors de la connexion à la caisse:", error)
      // TODO: Afficher un toast d'erreur
    } finally {
      setConnectingCashRegisterId(null)
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "active":
        return <CheckCircle className="h-4 w-4 text-green-500" />
      case "inactive":
        return <Monitor className="h-4 w-4 text-gray-400" />
      case "maintenance":
        return <Terminal className="h-4 w-4 text-orange-500" />
      case "retired":
        return <Wifi className="h-4 w-4 text-red-500" />
      default:
        return <Monitor className="h-4 w-4 text-gray-400" />
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "active":
        return "Active"
      case "inactive":
        return "Inactive"
      case "maintenance":
        return "En maintenance"
      case "retired":
        return "Retirée"
      default:
        return "Inconnu"
    }
  }

  const isCashRegisterLocked = (cashRegister: CashRegisterTable) => {
    return cashRegister.isLocked && cashRegister.currentUserId
  }

  const isCashRegisterAvailable = (cashRegister: CashRegisterTable) => {
    return (
      cashRegister.status === "active" && !isCashRegisterLocked(cashRegister)
    )
  }

  if (pendingCashRegisters) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            Chargement des caisses enregistreuses...
          </p>
        </div>
      </div>
    )
  }

  if (cashRegisters.length === 0) {
    return (
      <CardContent className="space-y-4 px-0">
        <Button
          onClick={onOpenAddModal}
          className="w-full flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          Créer une caisse enregistreuse
        </Button>
      </CardContent>
    )
  }

  return (
    <CardContent className="flex-1 overflow-y-auto space-y-4 px-0">
      {cashRegisters.map((cashRegister) => (
        <Card
          key={cashRegister.caisseId}
          className={`relative w-1/2 ${
            isCashRegisterLocked(cashRegister)
              ? "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/20"
              : isCashRegisterAvailable(cashRegister)
              ? "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950/20"
              : "border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-800/50"
          }`}
        >
          {/* Indicateur de verrouillage */}
          {isCashRegisterLocked(cashRegister) && (
            <div className="absolute top-2 right-2">
              <Lock className="h-4 w-4 text-red-500" />
            </div>
          )}

          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {getStatusIcon(cashRegister.status)}
                <CardTitle className="text-lg">
                  {cashRegister.name ||
                    `Caisse ${cashRegister.caisseId.slice(0, 8)}`}
                </CardTitle>
              </div>
              {cashRegister.readerId && (
                <Terminal className="h-4 w-4 text-indigo-500" />
              )}
            </div>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Informations de la caisse */}
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  Statut:
                </span>
                <span
                  className={`font-medium ${
                    cashRegister.status === "active"
                      ? "text-green-600"
                      : cashRegister.status === "maintenance"
                      ? "text-orange-600"
                      : cashRegister.status === "retired"
                      ? "text-red-600"
                      : "text-gray-600"
                  }`}
                >
                  {getStatusLabel(cashRegister.status)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">
                  Terminal:
                </span>
                <span className="font-medium">
                  {cashRegister.readerId ? "Connecté" : "Non connecté"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600 dark:text-gray-400">ID:</span>
                <span className="font-mono text-xs">
                  {cashRegister.caisseId.slice(0, 8)}...
                </span>
              </div>
            </div>

            {/* Bouton de connexion ou message d'info */}
            {isCashRegisterAvailable(cashRegister) ? (
              <Button
                onClick={() => handleConnectToCashRegister(cashRegister)}
                disabled={connectingCashRegisterId === cashRegister.caisseId}
                className="w-full flex items-center gap-2"
                size="sm"
              >
                {connectingCashRegisterId === cashRegister.caisseId ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Connexion...
                  </>
                ) : (
                  <>
                    <Terminal className="h-4 w-4" />
                    Se connecter
                  </>
                )}
              </Button>
            ) : isCashRegisterLocked(cashRegister) ? (
              <div className="text-center p-3 bg-red-100 dark:bg-red-900/20 rounded-md">
                <div className="flex items-center justify-center gap-2 text-red-700 dark:text-red-300">
                  <Lock className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Caisse occupée par un autre collaborateur
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-center p-3 bg-gray-100 dark:bg-gray-800 rounded-md">
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  Caisse non disponible ({getStatusLabel(cashRegister.status)})
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </CardContent>
  )
}
